// @owners { team: patients }
import { createQueryKeys } from '@lukemorales/query-key-factory';
import { get } from '~shared/helpers/apiHelper';
import { getErrorMessageFromResponse } from '~shared/helpers/helper';
import { staleTimes } from '~shared/queries/constants';
import { useQuery } from '~shared/react-query';
import { type QuantityOption } from '~shared/types/clients';

export const quantityOptions = createQueryKeys('quantityOptions', {
  byPrescription: ({ prescriptionID }: { prescriptionID: number }) => ({
    queryKey: [prescriptionID],
    queryFn: async (): Promise<QuantityOption[]> => {
      const response = await get(`/prescriptions/${prescriptionID}/quantity_options`, {}, { version: 'v3' });
      if (response.error) {
        throw new Error(getErrorMessageFromResponse(response));
      }
      return response.data;
    },
  }),
});

/**
 * Fetch the available quantity options for a prescription. Returns an empty list while
 * loading or on error, matching the prior Redux behavior.
 */
export const useQuantityOptions = (prescriptionID: number | null | undefined) => {
  const { data, isLoading } = useQuery({
    ...quantityOptions.byPrescription({ prescriptionID: prescriptionID ?? 0 }),
    enabled: !!prescriptionID,
    staleTime: staleTimes.fiveMinutes,
    gcTime: staleTimes.fiveMinutes,
  });

  return {
    options: data ?? [],
    isLoading,
  };
};
